import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Clock, Zap } from 'lucide-react';
import { useEffect, useState } from 'react';

interface VoteTimelineProps {
    start_timestamp_ms: number;
    minimum_duration_timestamp_ms: number;
    end_timestamp_ms: number;
    voteResult?: 'Pending' | 'Passed' | 'NotPassed';
}

export function VoteTimeline({ start_timestamp_ms, minimum_duration_timestamp_ms, end_timestamp_ms, voteResult }: VoteTimelineProps) {
    const [currentTime, setCurrentTime] = useState(Date.now());

    useEffect(() => {
        const timer = setInterval(() => {
            setCurrentTime(Date.now());
        }, 1000);
        return () => clearInterval(timer);
    }, []);

    const startTime = start_timestamp_ms;
    const minimumDurationTime = minimum_duration_timestamp_ms;
    const endTime = end_timestamp_ms;

    // Check if vote passed early
    const passedEarly = voteResult === 'Passed' && currentTime < endTime;

    const formatTimeRemaining = (targetTime: number): string => {
        const diff = targetTime - currentTime;
        if (diff <= 0) return 'Ended';

        const days = Math.floor(diff / (1000 * 60 * 60 * 24));
        const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((diff % (1000 * 60)) / 1000);

        if (days > 0) return `${days}d ${hours}h ${minutes}m ${seconds}s`;
        if (hours > 0) return `${hours}h ${minutes}m ${seconds}s`;
        if (minutes > 0) return `${minutes}m ${seconds}s`;
        return `${seconds}s`;
    };

    const formatDateTime = (timestamp: number) => {
        return new Date(timestamp).toLocaleString([], { dateStyle: 'short', timeStyle: 'short', timeZone: 'UTC' }) + ' UTC';
    };

    // Determine current status
    const isBeforeStart = currentTime < startTime;
    const isActive = currentTime >= startTime && currentTime < endTime && !passedEarly;
    const isEnded = currentTime >= endTime || passedEarly;
    const minimumDurationReached = currentTime >= minimumDurationTime;

    // Determine what countdowns to show
    const getCountdowns = () => {
        const items = [];

        if (passedEarly) {
            // Vote passed early
            items.push(
                { label: 'Voting started on', time: formatDateTime(startTime), type: 'past' },
                { label: 'Early passing was available from', time: formatDateTime(minimumDurationTime), type: 'past' }
            );
        } else if (isBeforeStart) {
            // Before voting starts
            items.push(
                { label: 'Voting starts in', time: formatTimeRemaining(startTime), type: 'countdown' },
                { label: 'Early passing available in', time: formatTimeRemaining(minimumDurationTime), type: 'countdown' },
                { label: 'Voting ends in', time: formatTimeRemaining(endTime), type: 'countdown' }
            );
        } else if (isActive) {
            // During active voting
            items.push(
                { label: 'Voting started on', time: formatDateTime(startTime), type: 'past' }
            );

            if (!minimumDurationReached) {
                items.push(
                    { label: 'Early passing available in', time: formatTimeRemaining(minimumDurationTime), type: 'countdown' }
                );
            } else {
                items.push(
                    { label: 'Early passing available since', time: formatDateTime(minimumDurationTime), type: 'past' }
                );
            }

            items.push(
                { label: 'Voting ends in', time: formatTimeRemaining(endTime), type: 'countdown' }
            );
        } else {
            // After voting ended
            items.push(
                { label: 'Voting started on', time: formatDateTime(startTime), type: 'past' },
                { label: 'Early passing was available from', time: formatDateTime(minimumDurationTime), type: 'past' },
                { label: 'Voting ended on', time: formatDateTime(endTime), type: 'past' }
            );
        }

        return items;
    };

    const countdowns = getCountdowns();

    // Calculate total voting duration
    const totalDuration = endTime - startTime;
    const formatDuration = (durationMs: number): string => {
        const days = Math.floor(durationMs / (1000 * 60 * 60 * 24));
        const hours = Math.floor((durationMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));

        if (days > 0 && hours === 0) {
            return `${days} day${days === 1 ? '' : 's'}`;
        } else if (days > 0) {
            return `${days}d ${hours}h`;
        } else {
            return `${hours} hour${hours === 1 ? '' : 's'}`;
        }
    };

    // Determine status color and label
    const getStatusInfo = () => {
        if (isBeforeStart) {
            return { status: 'not-started', color: 'bg-gray-500', label: 'Not Started' };
        }
        if (isActive) {
            return { status: 'active', color: 'bg-blue-500', label: 'Active' };
        }
        if (passedEarly) {
            return { status: 'passed-early', color: 'bg-green-500', label: 'Passed Early' };
        }
        return { status: 'ended', color: 'bg-gray-500', label: 'Ended' };
    };

    const statusInfo = getStatusInfo();

    return (
        <Card className='bg-[#C0E6FF]/10 backdrop-blur-md border border-white/20 rounded-lg shadow-lg'>
            <CardHeader>
                <CardTitle className="flex items-center gap-2">
                    <Clock className="h-5 w-5" />
                    Vote Timeline
                    {passedEarly && (
                        <Badge className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 ml-2">
                            <Zap className="h-3 w-3 mr-1" />
                            Passed Early
                        </Badge>
                    )}
                </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
                {/* Current Status */}
                <div className="space-y-4">
                    <div className={`flex flex-col gap-2 p-3 rounded-lg border ${statusInfo.status === 'active' ? 'bg-blue-50 border-blue-200 dark:bg-blue-950 dark:border-blue-800' :
                        statusInfo.status === 'passed-early' ? 'bg-green-50 border-green-200 dark:bg-green-950 dark:border-green-800' :
                            'bg-gray-50 border-gray-200 dark:bg-gray-950 dark:border-gray-800'
                        }`}>
                        <div className="flex items-start gap-3">
                            <div className={`w-3 h-3 rounded-full flex-shrink-0 mt-0.5 ${statusInfo.color}`} />
                            <div className="flex-1 min-w-0">
                                <div className="flex flex-wrap items-center gap-2 mb-1">
                                    <span className="font-medium">Voting Period ({formatDuration(totalDuration)})</span>
                                    <Badge
                                        variant={statusInfo.status === 'active' ? 'default' : statusInfo.status === 'passed-early' ? 'secondary' : 'outline'}
                                        className="text-xs"
                                    >
                                        {statusInfo.label}
                                    </Badge>
                                </div>
                                <div className="text-sm text-muted-foreground mb-2">
                                    {isActive ? 'Validators can vote on the proposal' :
                                        isBeforeStart ? 'Validators will be able to vote during this period' :
                                            'Validators could vote and change their votes during this period'}
                                </div>
                                {isActive && minimumDurationReached && (
                                    <div className="text-sm text-yellow-600 dark:text-yellow-400 flex items-center gap-1">
                                        <Zap className="h-3 w-3" />
                                        Early passing conditions can now be evaluated
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="text-xs text-muted-foreground pl-6">
                            {formatDateTime(startTime)} - {formatDateTime(endTime)}
                        </div>
                    </div>
                </div>

                {/* Countdowns */}
                <div className="grid grid-cols-1 gap-4">
                    {countdowns.map((countdown, index) => (
                        <div key={index} className="space-y-2">
                            <div className="text-sm font-medium">
                                {countdown.label}
                            </div>
                            <div className={`text-lg font-bold ${countdown.type === 'past' ? 'text-muted-foreground' :
                                countdown.type === 'current' ? 'text-green-600 dark:text-green-400' :
                                    'text-primary'
                                }`}>
                                {countdown.time}
                            </div>
                        </div>
                    ))}
                </div>
            </CardContent>
        </Card>
    );
} 